import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { attributeTypes } from 'utils';
import { resolveTableCell } from './goalModeTable';
import { isOwnedGoal } from '../utils/goalAttributes';

export const useAutoFillMentions = ({ setValue, getValues, formList }) => {
    const formDefault = useSelector((state) => state.forms.formDefault);
    const goalDefaultValues = useSelector((state) => state.goals.goalDefaultValues);
    // Read through a ref so a `formList` change doesn't re-run the pass below: it
    // would run before the host re-seeds its form from the new `formList` and write
    // into the stale form state (a newly picked goal's values in the legacy shape),
    // which the host's form→formList sync then copies back over the new `formList`.
    // The host calls `applyAutoFill` itself right after each re-seed.
    const formListRef = useRef(formList);
    formListRef.current = formList;

    const applyAutoFill = useCallback(() => {
        const formList = formListRef.current;
        if (!formDefault) return;

        const replaceMentions = (value, defaults) => {
            if (typeof value !== 'string') return value;
            const regex = /<span data-id="([^"]+)"[^>]*>.*?<\/span>/g;
            return value.replace(regex, (match, dataId) => {
                const val = defaults[dataId];
                if (val === null) return '';
                return val !== undefined ? val : match;
            });
        };

        const replaceAttrMentions = (attr, pathPrefix, defaults) => {
            if (
                (attr?.type === attributeTypes.RICH_TEXT || attr?.type === attributeTypes.INPUT || attr?.type === attributeTypes.TARGET) &&
                typeof attr?.value === 'string'
            ) {
                const currentValue = getValues(`${pathPrefix}.value`);
                const valueToReplace = typeof currentValue === 'string' ? currentValue : attr.value;
                const newValue = replaceMentions(valueToReplace, defaults);
                if (newValue !== valueToReplace) {
                    setValue(`${pathPrefix}.value`, newValue);
                }
            }

            // TABLE value is an array of row objects, each cell can contain HTML with mentions
            // Table filler mode uses plain <input> so we must strip HTML and decode entities
            if (attr?.type === attributeTypes.TABLE && Array.isArray(attr?.value)) {
                const currentTableValue = getValues(`${pathPrefix}.value`);
                const tableToReplace = Array.isArray(currentTableValue) ? currentTableValue : attr.value;
                let tableChanged = false;
                const newRows = tableToReplace.map((row) => {
                    const newRow = { ...row };
                    Object.keys(newRow).forEach((colId) => {
                        const replaced = resolveTableCell(newRow[colId], defaults);
                        if (replaced !== newRow[colId]) {
                            newRow[colId] = replaced;
                            tableChanged = true;
                        }
                    });
                    return newRow;
                });
                if (tableChanged) {
                    setValue(`${pathPrefix}.value`, newRows);
                }
            }
        };

        const currentSections = getValues('sections');
        if (currentSections) {
            currentSections.forEach((section, sIndex) => {
                if (section?.goalSection) {
                    // Goal sections: use goal-specific default values for each selected goal
                    const selectedGoals = formList?.sections?.[sIndex]?.selectedGoals || [];
                    selectedGoals.forEach((goal) => {
                        const goalDefaults = goalDefaultValues?.[goal?.id] || {};
                        const mergedDefaults = { ...formDefault, ...goalDefaults };
                        // A goal that owns its fields fills from them; a legacy goal from the
                        // section's fields (its values are written under the goal path).
                        const goalFields = isOwnedGoal(section, goal?.id)
                            ? section.goals[goal.id].attributes || []
                            : section?.attributes || [];
                        goalFields.forEach((attr, aIndex) => {
                            if (attr?.type === attributeTypes.TARGET && goal?.targets?.length) {
                                // For TARGET attributes, loop through each target
                                const rawGoalTargets = mergedDefaults?.GOAL_TARGETS || [];
                                const goalTargets = Array.isArray(rawGoalTargets) ? rawGoalTargets : Object.values(rawGoalTargets);
                                goal.targets.forEach((target, tIndex) => {
                                    // Match target data from goalDefaultValues by name, fallback to index
                                    const targetData =
                                        goalTargets?.find((t) => t.TARGET_NAME === target?.name) || goalTargets[tIndex] || {};
                                    const targetDefaults = {
                                        ...mergedDefaults,
                                        ...targetData,
                                    };
                                    const targetAttrPath = `sections.${sIndex}.goals.${goal?.id}.targets.${target?.id}.attributes.${aIndex}`;
                                    const currentTargetValue = getValues(`${targetAttrPath}.value`);
                                    const isTargetEmpty =
                                        currentTargetValue === undefined ||
                                        (Array.isArray(currentTargetValue) && currentTargetValue.length === 0);
                                    if (attr?.value !== undefined && isTargetEmpty) {
                                        setValue(`${targetAttrPath}.value`, attr.value);
                                    }
                                    replaceAttrMentions(attr, targetAttrPath, targetDefaults);
                                });
                            } else {
                                const goalAttrPath = `sections.${sIndex}.goals.${goal?.id}.attributes.${aIndex}`;
                                // Initialize the goal-specific attribute with the template value if not already set
                                const currentGoalValue = getValues(`${goalAttrPath}.value`);
                                const isEmpty =
                                    currentGoalValue === undefined || (Array.isArray(currentGoalValue) && currentGoalValue.length === 0);
                                if (attr?.value !== undefined && isEmpty) {
                                    setValue(`${goalAttrPath}.value`, attr.value);
                                }
                                replaceAttrMentions(attr, goalAttrPath, mergedDefaults);
                            }
                        });
                    });
                } else {
                    // Non-goal sections: use global formDefault
                    section?.attributes?.forEach((attr, aIndex) => {
                        // A Goal Mode field resolves only the general tokens here; its goal
                        // tokens are filled per picked goal (`GoalModeField`).
                        replaceAttrMentions(attr, `sections.${sIndex}.attributes.${aIndex}`, formDefault);
                    });
                }
            });
        }

        // Replace mentions in top-level attributes (non-section forms)
        const currentAttributes = getValues('attributes');
        if (currentAttributes) {
            currentAttributes.forEach((attr, aIndex) => {
                replaceAttrMentions(attr, `attributes.${aIndex}`, formDefault);
            });
        }

        // Replace mentions in header and footer content
        const headerContent = getValues('header.content');
        if (headerContent && typeof headerContent === 'string') {
            const newHeaderContent = replaceMentions(headerContent, formDefault);
            if (newHeaderContent !== headerContent) {
                setValue('header.content', newHeaderContent);
            }
        }

        const footerContent = getValues('footer.content');
        if (footerContent && typeof footerContent === 'string') {
            const newFooterContent = replaceMentions(footerContent, formDefault);
            if (newFooterContent !== footerContent) {
                setValue('footer.content', newFooterContent);
            }
        }
    }, [formDefault, goalDefaultValues]);

    useEffect(() => {
        applyAutoFill();
    }, [applyAutoFill]);

    return applyAutoFill;
};

export const fillMentions = (html, formDefault) => {
    if (!html || typeof html !== 'string' || !formDefault) return html;
    return html.replace(/<span[^>]*\bdata-(?:id|key)="([^"]+)"[^>]*>.*?<\/span>/g, (match, dataId) => {
        const val = formDefault[dataId] ?? formDefault[dataId.replace(/^@/, '')];
        return val != null ? val : match;
    });
};

// Origina;
// export const useAutoFillMentions = ({ setValue, getValues, formList }) => {
//     const formDefault = useSelector((state) => state.forms.formDefault);
//     const goalDefaultValues = useSelector((state) => state.goals.goalDefaultValues);
//
//     const applyAutoFill = useCallback(() => {
//         if (!formDefault) return;
//
//         const replaceMentions = (value, defaults) => {
//             if (typeof value !== 'string') return value;
//             const regex = /<span data-id="([^"]+)"[^>]*>.*?<\/span>/g;
//             return value.replace(regex, (match, dataId) => {
//                 const val = defaults[dataId];
//                 if (val === null) return '';
//                 return val !== undefined ? val : match;
//             });
//         };
//
//         const replaceAttrMentions = (attr, pathPrefix, defaults) => {
//             if (
//                 (attr?.type === attributeTypes.RICH_TEXT ||
//                     attr?.type === attributeTypes.INPUT ||
//                     attr?.type === attributeTypes.TARGET) &&
//                 typeof attr?.value === 'string'
//             ) {
//                 const newValue = replaceMentions(attr.value, defaults);
//                 if (newValue !== attr.value) {
//                     setValue(`${pathPrefix}.value`, newValue);
//                 }
//             }
//
//             // TABLE value is an array of row objects, each cell can contain HTML with mentions
//             // Table filler mode uses plain <input> so we must strip HTML and decode entities
//             if (attr?.type === attributeTypes.TABLE && Array.isArray(attr?.value)) {
//                 let tableChanged = false;
//                 const newRows = attr.value.map((row) => {
//                     const newRow = { ...row };
//                     Object.keys(newRow).forEach((colId) => {
//                         if (typeof newRow[colId] === 'string') {
//                             let replaced = replaceMentions(newRow[colId], defaults);
//                             if (replaced !== newRow[colId]) {
//                                 // Strip any remaining HTML tags
//                                 replaced = replaced.replace(/<[^>]*>/g, '');
//                                 // Decode HTML entities (&nbsp; &amp; etc.)
//                                 const txt = document.createElement('textarea');
//                                 txt.innerHTML = replaced;
//                                 replaced = txt.value;
//                                 newRow[colId] = replaced;
//                                 tableChanged = true;
//                             }
//                         }
//                     });
//                     return newRow;
//                 });
//                 if (tableChanged) {
//                     setValue(`${pathPrefix}.value`, newRows);
//                 }
//             }
//         };
//
//         const currentSections = getValues('sections');
//         if (currentSections) {
//             currentSections.forEach((section, sIndex) => {
//                 if (section?.goalSection) {
//                     // Goal sections: use goal-specific default values for each selected goal
//                     const selectedGoals = formList?.sections?.[sIndex]?.selectedGoals || [];
//                     selectedGoals.forEach((goal) => {
//                         const goalDefaults = goalDefaultValues?.[goal?.id] || {};
//                         const mergedDefaults = { ...formDefault, ...goalDefaults };
//                         section?.attributes?.forEach((attr, aIndex) => {
//                             if (attr?.type === attributeTypes.TARGET && goal?.targets?.length) {
//                                 // For TARGET attributes, loop through each target
//                                 const goalTargets = mergedDefaults?.GOAL_TARGETS || [];
//                                 goal.targets.forEach((target, tIndex) => {
//                                     // Match target data from goalDefaultValues by name, fallback to index
//                                     const targetData = goalTargets.find(t => t.TARGET_NAME === target?.name) || goalTargets[tIndex] || {};
//                                     const targetDefaults = {
//                                         ...mergedDefaults,
//                                         ...targetData,
//                                     };
//                                     const targetAttrPath = `sections.${sIndex}.goals.${goal?.id}.targets.${target?.id}.attributes.${aIndex}`;
//                                     if (attr?.value !== undefined && getValues(`${targetAttrPath}.value`) === undefined) {
//                                         setValue(`${targetAttrPath}.value`, attr.value);
//                                     }
//                                     replaceAttrMentions(attr, targetAttrPath, targetDefaults);
//                                 });
//                             } else {
//                                 const goalAttrPath = `sections.${sIndex}.goals.${goal?.id}.attributes.${aIndex}`;
//                                 // Initialize the goal-specific attribute with the template value if not already set
//                                 if (attr?.value !== undefined && getValues(`${goalAttrPath}.value`) === undefined) {
//                                     setValue(`${goalAttrPath}.value`, attr.value);
//                                 }
//                                 replaceAttrMentions(attr, goalAttrPath, mergedDefaults);
//                             }
//                         });
//                     });
//                 } else {
//                     // Non-goal sections: use global formDefault
//                     section?.attributes?.forEach((attr, aIndex) => {
//                         replaceAttrMentions(attr, `sections.${sIndex}.attributes.${aIndex}`, formDefault);
//                     });
//                 }
//             });
//         }
//
//         // Replace mentions in top-level attributes (non-section forms)
//         const currentAttributes = getValues('attributes');
//         if (currentAttributes) {
//             currentAttributes.forEach((attr, aIndex) => {
//                 replaceAttrMentions(attr, `attributes.${aIndex}`, formDefault);
//             });
//         }
//     }, [formDefault, goalDefaultValues]);
//
//     useEffect(() => {
//         applyAutoFill();
//     }, [applyAutoFill]);
//
//     return applyAutoFill;
// };
